import {skipToken} from '@reduxjs/toolkit/query';
import {addMonths, isWithinInterval, subMonths} from 'date-fns';
import {BigCalendarUser} from 'platform/big-calendar';

import {useEffect, useMemo, useState} from 'react';

import {isNotEmpty, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {calendarApi, datagridApi, SetMyCalendarSelectedUsersApiArg} from '@dms/api/core';
import {useGetCurrentUserInfoQuery, useGetUsersQuery} from '@dms/api/customer';
import {UserStateFilterSchema} from '@dms/api/shared';
import {DataQueryOptions, handleApiError, useLazyDataQuery} from '@dms/shared';

import {useQueryState, parseDate, getApiDateString, isNotNilOrEmpty} from 'shared/web';

import {getBigCalendarUser} from '../utils/getBigCalendarUser';
import {getNonHiddenSelectedUserIds} from '../utils/getNonHiddenSelectedUserIds';
import {getSelectedCalendarUsers} from '../utils/getSelectedCalendarUsers';
import {getUserColor} from '../utils/getUserColor';

export function useBigCalendarControl() {
  const [selectedDate, setSelectedDate] = useQueryState(SELECTED_DATE);
  const [dueDateFrom, setDueDateFrom] = useQueryState(DUE_DATE_FROM);
  const [dueDateTo, setDueDateTo] = useQueryState(DUE_DATE_TO);

  const parsedSelectedDate =
    isNotNil(selectedDate) && isNotEmpty(selectedDate) ? parseDate(selectedDate) : new Date();

  const [
    getLazyDataQuery,
    {data: dataQuery, isLoading: isLoadingGridQuery, isError: isGridQueryErrored},
  ] = useLazyDataQuery('task');

  const [
    getTestDriveLazyDataQuery,
    {
      data: testDriveDataQuery,
      isLoading: isLoadingTestDriveGridQuery,
      isError: isTestDriveGridQueryErrored,
    },
  ] = useLazyDataQuery('test-drive');

  const {
    data: tasks,
    isLoading: isGridLoading,
    isError: isGridErrored,
  } = datagridApi.useGetDataByDataQueryQuery(
    {
      gridCode: 'task',
      // eslint-disable-next-line typescript/no-non-null-asserted-optional-chain -- Muted pre-existing violation
      dataQueryId: dataQuery?.dataQueryId!,
      offset: 0,
      limit: 100000,
      body: {
        columns: [
          'dueDate',
          'dueTime',
          'assignedTo',
          'title',
          'priority',
          'type',
          'state',
          'reportedBy',
          'description',
          'relatedTo',
        ],
      },
    },
    {
      skip: isNilOrEmpty(dataQuery?.dataQueryId),
      refetchOnMountOrArgChange: true,
      pollingInterval: TASK_POLLING_INTERVAL,
    }
  );

  const {
    data: testDrives,
    isLoading: isTestDriveGridLoading,
    isError: isTestDriveGridErrored,
  } = datagridApi.useGetDataByDataQueryQuery(
    {
      gridCode: 'test-drive',
      // eslint-disable-next-line typescript/no-non-null-asserted-optional-chain -- Muted pre-existing violation
      dataQueryId: testDriveDataQuery?.dataQueryId!,
      offset: 0,
      limit: 100000,
      body: {
        columns: [
          'scheduledAt',
          'returnedAt',
          'state',
          'driver',
          'customer',
          'vehicle',
          'businessCase',
          'note',
        ],
      },
    },
    {
      skip: isNilOrEmpty(testDriveDataQuery?.dataQueryId),
      refetchOnMountOrArgChange: true,
      pollingInterval: TASK_POLLING_INTERVAL,
    }
  );

  const {data: currentUser} = useGetCurrentUserInfoQuery();

  const {
    data: calendarSelectedUsers,
    isLoading: isCalendarSelectedUsersLoading,
    isError: isCalendarSelectedUsersErrored,
  } = calendarApi.useGetMyCalendarSelectedUsersQuery(undefined, {
    skip: !currentUser,
  });

  const [setMyCalendarSelectedUsers] = calendarApi.useSetMyCalendarSelectedUsersMutation();

  const {
    data: usersData,
    isFetching: isLoadingUsers,
    isError: isUsersErrored,
  } = useGetUsersQuery({stateFilter: UserStateFilterSchema.enum.active});
  const [selectedUsers, setSelectedUsers] = useState<BigCalendarUser[]>([]);

  const selectedUsersLoadingFallback = selectedUsers && selectedUsers.length === 0;

  const nonHiddenSelectedUserIds = getNonHiddenSelectedUserIds(selectedUsers);

  /**
   * Standalone Outlook events, fetched over the same window and users as the two data grids.
   * A failure here must not blank the calendar, so it is reported apart from `isError` and
   * tasks and test drives keep rendering.
   */
  const {data: calendarEvents, isError: isCalendarEventsErrored} =
    calendarApi.useGetCalendarEventsQuery(
      isNotNilOrEmpty(dueDateFrom) &&
        isNotNilOrEmpty(dueDateTo) &&
        isNotEmpty(nonHiddenSelectedUserIds)
        ? {from: dueDateFrom, to: dueDateTo, userIds: nonHiddenSelectedUserIds}
        : skipToken,
      {refetchOnMountOrArgChange: true, pollingInterval: TASK_POLLING_INTERVAL}
    );

  const onFilterChange = (filteredUserIds: string[], date: Date) => {
    const dateFrom = subMonths(date, 3);
    const dateTo = addMonths(date, 3);

    const taskArgs: DataQueryOptions = {
      filters: {
        assignedTo: filteredUserIds,
        dueDate: {
          from: getApiDateString(dateFrom),
          to: getApiDateString(dateTo),
        },
      },
    };

    const testDriveArgs: DataQueryOptions = {
      filters: {
        driver: filteredUserIds,
        scheduledAt: {
          from: getApiDateString(dateFrom),
          to: getApiDateString(dateTo),
        },
      },
    };

    getLazyDataQuery(taskArgs);
    getTestDriveLazyDataQuery(testDriveArgs);
    setDueDateFrom(getApiDateString(dateFrom));
    setDueDateTo(getApiDateString(dateTo));
    setSelectedDate(getApiDateString(date));
  };

  /**
   * Handles changes in user filter selection
   * Assigns colors to selected users and triggers the filter change callback
   * @param {BigCalendarUser[]} selectedUsers - Array of newly selected users
   */
  const onUserControlChange = (selectedUsers: BigCalendarUser[]) => {
    const selectedUsersWithColor = selectedUsers.map((user, index) => ({
      ...user,
      color: getUserColor(index),
    }));
    setSelectedUsers(selectedUsersWithColor);

    const args: SetMyCalendarSelectedUsersApiArg = {
      setMyCalendarSelectedUsersRequestBody: {
        selectedUsers: selectedUsersWithColor.map((user) => ({
          userId: user.id,
          color: user.color,
          isHidden: !!user.isHidden,
        })),
      },
    };

    setMyCalendarSelectedUsers(args)
      .unwrap()
      .then(() => {
        onFilterChange(getNonHiddenSelectedUserIds(selectedUsersWithColor), parsedSelectedDate);
      })
      .catch(handleApiError);
  };

  /**
   * Handles calendar date navigation
   * Updates the current date in URL query params and triggers the date change callback
   * @param {Date} date - New selected date
   */
  const onNavigate = (date: Date) => {
    if (
      isNotNil(dueDateFrom) &&
      isNotEmpty(dueDateFrom) &&
      isNotNil(dueDateTo) &&
      isNotEmpty(dueDateTo)
    ) {
      const isDateInRange = isWithinInterval(parseDate(date), {
        start: parseDate(dueDateFrom),
        end: parseDate(dueDateTo),
      });

      if (isDateInRange) {
        setSelectedDate(getApiDateString(date));
        return;
      }
    }

    onFilterChange(getNonHiddenSelectedUserIds(selectedUsers), date);
  };

  useEffect(
    function syncSelectedCalendarUsers() {
      if (isNotNil(calendarSelectedUsers) && isNotNil(users) && selectedUsersLoadingFallback) {
        const calendarUsers = getSelectedCalendarUsers({
          usersData,
          selectedUsers: calendarSelectedUsers,
          currentUserData: currentUser,
        });
        setSelectedUsers(calendarUsers);
        onFilterChange(getNonHiddenSelectedUserIds(calendarUsers), parsedSelectedDate);
      }
    },
    // eslint-disable-next-line react/exhaustive-deps
    [calendarSelectedUsers, usersData]
  );

  const users = useMemo(
    () => usersData?.filter((user) => !user?.blocked).map((user) => getBigCalendarUser(user)),
    [usersData]
  );

  return {
    currentUser: currentUser ? getBigCalendarUser(currentUser) : null,
    users,
    selectedUsers,
    tasks,
    testDrives,
    calendarEvents,
    isCalendarEventsErrored,
    isLoading:
      isLoadingGridQuery ||
      isGridLoading ||
      isLoadingTestDriveGridQuery ||
      isTestDriveGridLoading ||
      isLoadingUsers ||
      isCalendarSelectedUsersLoading ||
      selectedUsersLoadingFallback,
    isError:
      isGridQueryErrored ||
      isGridErrored ||
      isTestDriveGridQueryErrored ||
      isTestDriveGridErrored ||
      isUsersErrored ||
      isCalendarSelectedUsersErrored,
    selectedDate: parsedSelectedDate,
    onNavigate,
    onUserControlChange,
  };
}

/** @constant {string} SELECTED_DATE - Query parameter key for storing current calendar date */
const SELECTED_DATE = 'selectedDate';

/** @constant {string} DUE_DATE_FROM - Query parameter key for storing due date from */
const DUE_DATE_FROM = 'dueDateFrom';

/** @constant {string} DUE_DATE_TO - Query parameter key for storing due date to */
const DUE_DATE_TO = 'dueDateTo';

/**
 * @constant {number} TASK_POLLING_INTERVAL - Polling interval for task updates in milliseconds
 */
const TASK_POLLING_INTERVAL = 15 * 60 * 1000;
